<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
               <h4 class="card-title">Venue Metrics</h4>
            </div>
            <div class="card-body">
              <b-overlay :show="isLoading" rounded="sm">
                <div class="table-responsive">
                    <vue-good-table
                        compactMode
                        styleClass="vgt-table condensed"
                        :columns="columns"
                        :rows="groupedData"
                        :pagination-options="{
                        enabled: true,
                        perPage: 100
                        }"
                        :search-options="{
                        enabled: true,
                        trigger: 'enter'
                        }"
                        :group-options="{
                        enabled: true,
                        collapsable: true,
                        headerPosition: 'top'
                        }"
                    >
                        <template slot="table-actions">
                        <b-button @click="toggleSortOrder" class="mb-3 small-button">
                            Sort by Last Event Date: {{ sortOrder === 'asc' ? 'Ascending' : 'Descending' }}
                        </b-button>
                        </template>
                    </vue-good-table>
                </div>
              </b-overlay>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table';

export default {
  name: 'VenueMetrics',
  components: {
    VueGoodTable,
  },
  data() {
    return {
      isLoading: false,
      listings: [],
      groupedData: [],
      sortOrder: 'asc',
      columns: [
        { label: "Venue", field: "venue" },
        { label: "Event Name", field: "eventName" },
        { label: "Events Listed", field: "distinctEvents" },
        { label: "Last Event Date", field: "lastEventDate" },
        { label: "Total Listings", field: "totalListings" },
        { label: "Total Tickets", field: "totalQuantity" },
        { label: "Average List Price", field: "averageListPrice" },
        { label: "Average Unit Cost", field: "averageUnitCost" },
      ],
    };
  },
  methods: {
    async fetchListings() {
      this.isLoading = true;
      try {
        const currentDate = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
        const response = await fetch(
          "https://skybox.vividseats.com/services/inventory?ticketStatus=AVAILABLE&eventDateFrom=" + currentDate,
          {
            headers: {
              "accept": "application/json",
              "X-Application-Token": "ff42e57f-1171-4ff9-a452-aa7ae5f9fdde",
              "X-Api-Token": "984730ee-a2e3-4f8b-9726-34f2b2ff0598",
            },
          }
        );
        
        const data = await response.json();
        this.listings = data['rows'];
        this.calculateVenueMetrics(data);
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        console.error("Error fetching listings:", error);
      }
    },
    calculateVenueMetrics(data) {
      const venueMetrics = {};

      data.rows.forEach((row) => {
        const venueName = row.event.venue.name;
        const eventDate = new Date(row.event.date);
        const eventName = row.event.name;

        if (!venueMetrics[venueName]) {
          venueMetrics[venueName] = {
            venue: venueName,
            totalListings: 0,
            totalQuantity: 0,
            totalListPrice: 0,
            totalUnitCost: 0,
            lastEventDate: eventDate,
            distinctEventsSet: new Set(),
            events: {},
          };
        }

        if (!venueMetrics[venueName].events[eventName]) {
          venueMetrics[venueName].events[eventName] = {
            eventName: eventName,
            totalListings: 0,
            totalQuantity: 0,
            totalListPrice: 0,
            totalUnitCost: 0,
            lastEventDate: eventDate,
          };
        }

        const venue = venueMetrics[venueName];
        const eventMetrics = venue.events[eventName];

        venue.totalListings += 1;
        venue.totalQuantity += row.quantity;
        venue.totalListPrice += row.listPrice * row.quantity;
        venue.totalUnitCost += row.unitCostAverage * row.quantity;
        venue.distinctEventsSet.add(eventName);

        eventMetrics.totalListings += 1;
        eventMetrics.totalQuantity += row.quantity;
        eventMetrics.totalListPrice += row.listPrice * row.quantity;
        eventMetrics.totalUnitCost += row.unitCostAverage * row.quantity;

        if (eventDate > eventMetrics.lastEventDate) {
          eventMetrics.lastEventDate = eventDate;
        }

        if (eventDate > venue.lastEventDate) {
          venue.lastEventDate = eventDate;
        }
      });

      this.groupedData = Object.values(venueMetrics)
        .map((venue) => ({
          ...venue,
          averageListPrice: (venue.totalListPrice / venue.totalQuantity).toFixed(2),
          averageUnitCost: (venue.totalUnitCost / venue.totalQuantity).toFixed(2),
          lastEventDate: venue.lastEventDate.toLocaleDateString("en-CA", { timeZone: "America/New_York" }),
          distinctEvents: venue.distinctEventsSet.size,
          children: Object.values(venue.events).map((event) => ({
            ...event,
            averageListPrice: (event.totalListPrice / event.totalQuantity).toFixed(2),
            averageUnitCost: (event.totalUnitCost / event.totalQuantity).toFixed(2),
            lastEventDate: event.lastEventDate.toLocaleDateString("en-CA", { timeZone: "America/New_York" }),
          })),
        }))
        .sort((a, b) => this.sortOrder === 'asc' ? new Date(a.lastEventDate) - new Date(b.lastEventDate) : new Date(b.lastEventDate) - new Date(a.lastEventDate)); // Sort by last event date
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      this.calculateVenueMetrics({ rows: this.listings });
    },
  },
  mounted() {
    this.fetchListings();
  },
};
</script>

<style scoped>
.container-fluid {
  padding-top: 20px;
}

.small-button {
  font-size: 12px;
  background-color: #c1cad6;
  border-color: #cbd5e0;
  padding: 5px 10px;
}
</style>
